import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import RichTextEditor from '../components/shared/RichTextEditor';
import { htmlToPlainText } from '../utils/richText';
import {
  Briefcase,
  Building2,
  CheckCircle2,
  FileUp,
  Loader2,
  Mail,
  ShoppingCart,
  Trash2,
  Users,
} from 'lucide-react';
import { API_BASE, apiRequest } from '../utils/api';
import ProcessingDisclaimerModal, { useProcessingDisclaimer } from '../components/shared/ProcessingDisclaimerModal';
import { useAuth } from '../components/auth/AuthComponents';
import { canUseHiringShortlist } from '../utils/userPermissions';

const DRAFT_KEY = 'docxsai-shortlist-draft';
const FILE_DB = 'docxsai-shortlist-files';
const FILE_STORE = 'cvs';
const HARD_MAX_UPLOAD = 300;

const formatNaira = (amount) =>
  new Intl.NumberFormat('en-NG', {
    style: 'currency',
    currency: 'NGN',
    maximumFractionDigits: 0,
  }).format(Number(amount) || 0);

function packCovers(pack, n) {
  if (!pack || n < 1) return false;
  if (pack.pricing === 'per_cv') {
    return n >= (pack.min_cvs || 150) && n <= HARD_MAX_UPLOAD;
  }
  return n <= (pack.credits || 0);
}

function packAmount(pack, n) {
  if (!pack) return 0;
  if (pack.pricing === 'per_cv') {
    return n * (pack.unit_price || 150);
  }
  return Number(pack.amount) || 0;
}

function openFileDb() {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(FILE_DB, 1);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(FILE_STORE)) {
        db.createObjectStore(FILE_STORE);
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

async function saveDraftFiles(files) {
  const db = await openFileDb();
  const payloads = await Promise.all(
    files.map(
      (f) =>
        new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () =>
            resolve({
              name: f.name,
              type: f.type,
              lastModified: f.lastModified,
              buffer: reader.result,
            });
          reader.onerror = () => reject(reader.error);
          reader.readAsArrayBuffer(f);
        }),
    ),
  );
  await new Promise((resolve, reject) => {
    const tx = db.transaction(FILE_STORE, 'readwrite');
    tx.objectStore(FILE_STORE).put(payloads, 'current');
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
  db.close();
}

async function loadDraftFiles() {
  const db = await openFileDb();
  const payloads = await new Promise((resolve, reject) => {
    const tx = db.transaction(FILE_STORE, 'readonly');
    const req = tx.objectStore(FILE_STORE).get('current');
    req.onsuccess = () => resolve(req.result || []);
    req.onerror = () => reject(req.error);
  });
  db.close();
  return (payloads || []).map(
    (p) => new File([p.buffer], p.name, { type: p.type || 'application/pdf', lastModified: p.lastModified }),
  );
}

async function clearDraftFiles() {
  try {
    const db = await openFileDb();
    await new Promise((resolve, reject) => {
      const tx = db.transaction(FILE_STORE, 'readwrite');
      tx.objectStore(FILE_STORE).delete('current');
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
    });
    db.close();
  } catch {
    /* ignore */
  }
}

const HiringShortlistPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const [entitlement, setEntitlement] = useState(null);
  const [runs, setRuns] = useState([]);
  const [packs, setPacks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [buyingPack, setBuyingPack] = useState(null);
  const [jobRole, setJobRole] = useState('');
  const [jobDescriptionHtml, setJobDescriptionHtml] = useState('');
  const [files, setFiles] = useState([]);
  const [selectedPackCode, setSelectedPackCode] = useState(null); // 'free' | pack code
  const [packError, setPackError] = useState('');
  const [step, setStep] = useState('form'); // form | preview | compare
  const [paymentTxRef, setPaymentTxRef] = useState(null);
  const [paymentStatus, setPaymentStatus] = useState(null); // success | failed | null
  const {
    open: disclaimerOpen,
    requestProcessing,
    confirmProcessing,
    dismissDisclaimer,
  } = useProcessingDisclaimer();

  const jdPlain = useMemo(() => htmlToPlainText(jobDescriptionHtml), [jobDescriptionHtml]);
  const freeRemaining = entitlement?.free_remaining ?? 0;
  const freeDailyLimit = entitlement?.free_daily_limit ?? 10;
  const freeUsedToday = entitlement?.free_used_today ?? 0;
  const maxUpload = entitlement?.max_upload_now ?? HARD_MAX_UPLOAD;
  const n = files.length;
  const freeCovers = n > 0 && freeRemaining >= n;

  const selectedPack = useMemo(() => {
    if (selectedPackCode === 'free') return null;
    return packs.find((p) => p.code === selectedPackCode) || null;
  }, [packs, selectedPackCode]);

  const estimatedAmount = useMemo(() => {
    if (selectedPackCode === 'free') return 0;
    return packAmount(selectedPack, n);
  }, [selectedPackCode, selectedPack, n]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [ent, runData, packData] = await Promise.all([
        apiRequest('hiring-shortlist/entitlement'),
        apiRequest('hiring-shortlist/runs'),
        apiRequest('hiring-shortlist/packs').catch(() => ({ packs: [] })),
      ]);
      setEntitlement(ent);
      setRuns(runData?.runs || []);
      setPacks(packData?.packs || []);
    } catch (err) {
      toast.error(err.message || 'Failed to load hiring shortlist');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (user && !canUseHiringShortlist(user.role)) {
      toast.error('Hiring Shortlist is available for business accounts only.');
      navigate('/dashboard');
    }
  }, [user, navigate]);

  useEffect(() => {
    load();
  }, [load]);

  // Restore draft + payment return query
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const params = new URLSearchParams(location.search);
      const payment = params.get('payment');
      const txRef = params.get('tx_ref');

      try {
        const raw = sessionStorage.getItem(DRAFT_KEY);
        if (raw) {
          const draft = JSON.parse(raw);
          if (!cancelled) {
            if (draft.jobRole) setJobRole(draft.jobRole);
            if (draft.jobDescriptionHtml) setJobDescriptionHtml(draft.jobDescriptionHtml);
            if (draft.selectedPackCode) setSelectedPackCode(draft.selectedPackCode);
          }
        }
        const restored = await loadDraftFiles();
        if (!cancelled && restored.length) setFiles(restored);
      } catch {
        /* ignore restore errors */
      }

      if (!cancelled && txRef && (payment === 'success' || payment === 'successful' || payment === 'failed')) {
        setPaymentTxRef(txRef);
        if (payment === 'failed') {
          setPaymentStatus('failed');
          setStep('preview');
          toast.error('Payment failed. You can retry checkout from preview.');
        } else {
          setPaymentStatus('success');
          setSelectedPackCode((prev) => (prev === 'free' ? prev : prev));
          setStep('compare');
          toast.success('Payment confirmed. Review your files and click Compare.');
        }
        navigate('/hiring-shortlist', { replace: true });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [location.search, navigate]);

  // Auto-select free when quota covers uploads
  useEffect(() => {
    if (step !== 'form') return;
    if (n < 1) {
      setSelectedPackCode(null);
      setPackError('');
      return;
    }
    if (freeCovers) {
      setSelectedPackCode('free');
      setPackError('');
      return;
    }
    if (selectedPackCode === 'free') {
      setSelectedPackCode(null);
    }
  }, [n, freeCovers, step, selectedPackCode]);

  const persistDraft = async () => {
    sessionStorage.setItem(
      DRAFT_KEY,
      JSON.stringify({
        jobRole,
        jobDescriptionHtml,
        selectedPackCode,
        cvCount: n,
      }),
    );
    if (files.length) await saveDraftFiles(files);
  };

  const clearDraft = async () => {
    sessionStorage.removeItem(DRAFT_KEY);
    await clearDraftFiles();
  };

  const validateFormBasics = () => {
    if (!entitlement?.can_start) {
      toast.error('Set your company name in Profile first.');
      return false;
    }
    if (!jobRole.trim() || jdPlain.length < 40) {
      toast.error('Enter a job role and a fuller job description (headings/bullets welcome).');
      return false;
    }
    if (files.length < 1) {
      toast.error('Upload at least one CV.');
      return false;
    }
    return true;
  };

  const goPreview = async (e) => {
    e.preventDefault();
    if (!validateFormBasics()) return;

    if (freeCovers) {
      setSelectedPackCode('free');
      setPackError('');
      await persistDraft();
      setStep('preview');
      return;
    }

    if (!selectedPackCode || selectedPackCode === 'free') {
      setPackError('Select a pack that covers your uploaded CVs, or reduce the upload count.');
      return;
    }
    if (!packCovers(selectedPack, n)) {
      setPackError('Selected pack does not cover this CV count. Pick a higher pack or remove CVs.');
      return;
    }
    setPackError('');
    await persistDraft();
    setStep('preview');
  };

  const startCheckout = async () => {
    if (!selectedPack || selectedPackCode === 'free') return;
    if (!packCovers(selectedPack, n)) {
      toast.error('Selected pack does not cover this CV count.');
      return;
    }
    setBuyingPack(selectedPack.code);
    try {
      await persistDraft();
      const apiRoot = API_BASE.replace(/\/api\/?$/, '');
      const redirectUrl = `${apiRoot}/api/payments/callback`;
      const res = await apiRequest('hiring-shortlist/packs/checkout', {
        method: 'POST',
        body: {
          pack_code: selectedPack.code,
          cv_count: n,
          redirect_url: redirectUrl,
          client_platform: 'web',
        },
      });
      if (!res.payment_link) {
        throw new Error(res.message || 'No payment link returned');
      }
      window.location.href = res.payment_link;
    } catch (err) {
      toast.error(err.message || 'Could not start pack checkout');
      setBuyingPack(null);
    }
  };

  const startRun = async () => {
    if (!validateFormBasics()) return;
    if (selectedPackCode !== 'free' && !paymentTxRef) {
      toast.error('Complete payment first, then click Compare.');
      return;
    }
    if (selectedPackCode !== 'free' && paymentStatus !== 'success') {
      toast.error('Payment was not successful. Retry checkout.');
      return;
    }

    const form = new FormData();
    form.append('job_role', jobRole.trim());
    form.append('job_description', jdPlain);
    files.forEach((f) => form.append('files', f));
    if (paymentTxRef && selectedPackCode !== 'free') {
      form.append('payment_tx_ref', paymentTxRef);
    }

    setSubmitting(true);
    try {
      const res = await apiRequest('hiring-shortlist/runs', { method: 'POST', body: form });
      toast.success(res.message || 'Shortlist queued. You will receive the PDF report by email.');
      if (res.rejected?.length) {
        res.rejected.forEach((r) => toast.warn(`${r.filename}: ${r.reason}`));
      }
      setFiles([]);
      setJobRole('');
      setJobDescriptionHtml('');
      setSelectedPackCode(null);
      setPaymentTxRef(null);
      setPaymentStatus(null);
      setStep('form');
      await clearDraft();
      await load();
    } catch (err) {
      toast.error(err.message || 'Failed to start shortlist');
    } finally {
      setSubmitting(false);
    }
  };

  const handleCompareClick = (e) => {
    e.preventDefault();
    requestProcessing(() => startRun());
  };

  const acceptedNames = useMemo(() => new Set(files.map((f) => f.name.toLowerCase())), [files]);

  const onPickFiles = (e) => {
    const picked = Array.from(e.target.files || []);
    e.target.value = '';
    if (!picked.length) return;

    const next = [...files];
    const errors = [];
    for (const file of picked) {
      if (next.length >= maxUpload) {
        errors.push(`Limit is ${maxUpload} CV(s) per run.`);
        break;
      }
      if (
        acceptedNames.has(file.name.toLowerCase()) ||
        next.some((f) => f.name.toLowerCase() === file.name.toLowerCase())
      ) {
        errors.push(`${file.name}: duplicate filename`);
        continue;
      }
      if (file.size < 100) {
        errors.push(`${file.name}: empty file`);
        continue;
      }
      next.push(file);
    }
    setFiles(next);
    setPaymentTxRef(null);
    setPaymentStatus(null);
    if (step === 'compare') setStep('form');
    errors.forEach((msg) => toast.warn(msg));
  };

  const removeFile = (name) => {
    setFiles((prev) => prev.filter((f) => f.name !== name));
    setPaymentTxRef(null);
    setPaymentStatus(null);
    if (step === 'compare') setStep('form');
  };

  const pickPack = (code) => {
    const pack = packs.find((p) => p.code === code);
    if (!packCovers(pack, n)) {
      setPackError(
        `${pack?.name || 'Pack'} does not cover ${n} CV(s). Pick a higher pack or reduce the CV upload.`,
      );
      setSelectedPackCode(code);
      return;
    }
    setPackError('');
    setSelectedPackCode(code);
  };

  if (loading) {
    return (
      <div className="h-full p-6 flex items-center justify-center text-[var(--color-text-muted)]">
        <Loader2 className="w-6 h-6 animate-spin mr-2" /> Loading hiring shortlist…
      </div>
    );
  }

  const editorDisabled = !entitlement?.can_start || submitting || step === 'preview' || step === 'compare';
  const showPaidPacks = n > 0 && !freeCovers && step === 'form';

  return (
    <div className="h-full p-6 overflow-y-auto">
      <ProcessingDisclaimerModal
        open={disclaimerOpen}
        onConfirm={confirmProcessing}
        onClose={dismissDisclaimer}
        actionLabel="Compare & email PDF report"
      />
      <div className="max-w-7xl mx-auto w-full">
        <div className="mb-8">
          <h1 className="text-3xl sm:text-4xl font-bold text-[var(--color-text)] mb-2 leading-tight">
            Hiring <span className="text-blue-400">Shortlist</span>
          </h1>
          <p className="text-[var(--color-text-muted)] w-full">
            Compare candidates at scale. Upload CVs against one role and JD — free for up to {freeDailyLimit}{' '}
            CVs per day, or pay on the go for larger batches. DocxsAi analyzes each CV, then emails a ranked
            comparison PDF.
          </p>
        </div>

        {entitlement?.company_required ? (
          <div className="mb-6 rounded-2xl border border-amber-500/30 bg-amber-500/10 p-5">
            <div className="flex items-start gap-3">
              <Building2 className="w-5 h-5 text-amber-400 mt-0.5" />
              <div>
                <p className="text-[var(--color-text)] font-semibold">Company name required</p>
                <p className="text-[var(--color-text-secondary)] text-sm mt-1">
                  Set your business name in Profile once. After that it cannot be edited.
                </p>
                <button
                  type="button"
                  onClick={() => navigate('/profile')}
                  className="mt-3 px-4 py-2 rounded-lg bg-blue-600 hover:bg-blue-500 text-[var(--color-text)] text-sm font-medium"
                >
                  Go to Profile
                </button>
              </div>
            </div>
          </div>
        ) : null}

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
          <div className="rounded-xl border border-[var(--color-border)] bg-[var(--color-surface-muted)]/50 p-4">
            <p className="text-xs text-[var(--color-text-muted)] uppercase tracking-wide">Company</p>
            <p className="text-[var(--color-text)] font-semibold mt-1">{entitlement?.company_name || 'Not set'}</p>
          </div>
          <div className="rounded-xl border border-[var(--color-border)] bg-[var(--color-surface-muted)]/50 p-4">
            <p className="text-xs text-[var(--color-text-muted)] uppercase tracking-wide">Free today</p>
            <p className="text-[var(--color-text)] font-semibold mt-1">
              {freeUsedToday}/{freeDailyLimit} used · {freeRemaining} left
            </p>
          </div>
          <div className="rounded-xl border border-[var(--color-border)] bg-[var(--color-surface-muted)]/50 p-4">
            <p className="text-xs text-[var(--color-text-muted)] uppercase tracking-wide">This batch</p>
            <p className="text-[var(--color-text)] font-semibold mt-1">
              {n} CV{n === 1 ? '' : 's'}
              {selectedPackCode === 'free'
                ? ' · Free'
                : selectedPack
                  ? ` · ${selectedPack.name}`
                  : ''}
            </p>
          </div>
        </div>

        {step === 'form' ? (
          <form onSubmit={goPreview} className="rounded-2xl border border-[var(--color-border)] bg-[var(--color-surface-muted)]/50 p-6 space-y-5 mb-8">
            <div>
              <label className="block text-sm font-medium text-[var(--color-text-secondary)] mb-2">Job role</label>
              <div className="relative">
                <Briefcase className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[var(--color-text-muted)]" />
                <input
                  value={jobRole}
                  onChange={(e) => setJobRole(e.target.value)}
                  disabled={editorDisabled}
                  placeholder="e.g. Senior Backend Engineer"
                  className="w-full pl-10 pr-4 py-3 themed-input border border-[var(--color-border)]/40 rounded-xl text-[var(--color-text)] placeholder-[var(--color-placeholder)] focus:outline-none focus:ring-2 focus:ring-[var(--color-ring)]/40"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-[var(--color-text-secondary)] mb-2">Job description</label>
              <RichTextEditor
                value={jobDescriptionHtml}
                onChange={setJobDescriptionHtml}
                readOnly={editorDisabled}
                placeholder="Outline the JD with headings, bullets, and responsibilities…"
                minHeight={220}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-[var(--color-text-secondary)] mb-2">
                Candidate CVs · {files.length}/{maxUpload}
              </label>
              <label
                className={`flex flex-col items-center justify-center gap-2 rounded-xl border border-dashed border-[var(--color-border)] elevated-card px-4 py-8 cursor-pointer ${
                  !entitlement?.can_start || files.length >= maxUpload
                    ? 'opacity-50 pointer-events-none'
                    : 'hover:border-blue-400'
                }`}
              >
                <FileUp className="w-6 h-6 text-blue-400" />
                <span className="text-sm text-[var(--color-text-secondary)]">
                  Free: up to {freeRemaining} left today · Paid packs for larger batches (max {maxUpload})
                </span>
                <input
                  type="file"
                  accept=".pdf,.doc,.docx,.txt,application/pdf"
                  multiple
                  className="hidden"
                  disabled={!entitlement?.can_start || files.length >= maxUpload || submitting}
                  onChange={onPickFiles}
                />
              </label>

              {files.length > 0 ? (
                <ul className="mt-3 space-y-2">
                  {files.map((f) => (
                    <li
                      key={f.name}
                      className="flex items-center justify-between rounded-lg themed-input border border-[var(--color-border)] px-3 py-2 text-sm text-[var(--color-text-secondary)]"
                    >
                      <span className="truncate pr-3">{f.name}</span>
                      <button
                        type="button"
                        onClick={() => removeFile(f.name)}
                        className="text-[var(--color-text-muted)] hover:text-red-400"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </li>
                  ))}
                </ul>
              ) : null}
            </div>

            {freeCovers && n > 0 ? (
              <div className="rounded-xl border border-emerald-500/30 bg-emerald-500/10 px-4 py-3 text-sm text-emerald-100">
                Free daily quota covers these {n} CV(s). Package set to Free.
              </div>
            ) : null}

            {showPaidPacks ? (
              <div>
                <h2 className="text-lg font-semibold text-[var(--color-text)] mb-1">Choose a pack</h2>
                <p className="text-sm text-[var(--color-text-muted)] mb-3">
                  Free quota is not enough for {n} CVs. Pick a pack that covers this batch (pay on checkout).
                </p>
                {packError ? (
                  <p className="mb-3 text-sm text-amber-300">{packError}</p>
                ) : null}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {packs.map((pack) => {
                    const covers = packCovers(pack, n);
                    const amount =
                      pack.pricing === 'per_cv' ? formatNaira(packAmount(pack, n)) : formatNaira(pack.amount);
                    const selected = selectedPackCode === pack.code;
                    return (
                      <button
                        key={pack.code}
                        type="button"
                        onClick={() => pickPack(pack.code)}
                        className={`text-left rounded-2xl border p-5 flex flex-col transition ${
                          selected
                            ? 'border-blue-400 bg-blue-500/20 ring-1 ring-blue-400/40'
                            : pack.highlight
                              ? 'border-blue-500/40 bg-blue-500/10'
                              : 'border-[var(--color-border)] elevated-card'
                        } ${!covers ? 'opacity-70' : ''}`}
                      >
                        {pack.highlight ? (
                          <span className="text-[11px] uppercase tracking-wide text-blue-300 font-semibold mb-2">
                            Most popular
                          </span>
                        ) : (
                          <span className="text-[11px] text-transparent mb-2">.</span>
                        )}
                        <h3 className="text-[var(--color-text)] font-semibold text-lg">{pack.name}</h3>
                        <p className="text-2xl font-bold text-[var(--color-text)] mt-2">
                          {pack.pricing === 'per_cv' && n < (pack.min_cvs || 150)
                            ? `₦150 / CV · min ${pack.min_cvs || 150}`
                            : amount}
                        </p>
                        <p className="text-sm text-blue-300 font-medium mt-1">
                          {pack.pricing === 'per_cv'
                            ? `${pack.min_cvs || 150}+ CVs`
                            : `Up to ${pack.credits} CVs`}
                        </p>
                        <p className="text-sm text-[var(--color-text-muted)] mt-3 flex-1">{pack.description}</p>
                        {!covers ? (
                          <p className="mt-3 text-xs text-amber-300">Does not cover {n} CVs</p>
                        ) : selected ? (
                          <p className="mt-3 text-xs text-emerald-300">Selected</p>
                        ) : null}
                      </button>
                    );
                  })}
                </div>
              </div>
            ) : null}

            <button
              type="submit"
              disabled={!entitlement?.can_start || submitting || files.length < 1}
              className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-6 py-3 rounded-xl bg-blue-600 hover:bg-blue-500 disabled:opacity-50 text-[var(--color-text)] font-semibold"
            >
              Proceed to preview
            </button>
          </form>
        ) : null}

        {(step === 'preview' || step === 'compare') && (
          <div className="rounded-2xl border border-[var(--color-border)] bg-[var(--color-surface-muted)]/50 p-6 space-y-5 mb-8">
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <h2 className="text-xl font-semibold text-[var(--color-text)]">
                {step === 'compare' ? 'Ready to compare' : 'Preview'}
              </h2>
              <button
                type="button"
                onClick={() => {
                  setStep('form');
                  setPaymentStatus(null);
                }}
                className="text-sm text-blue-300 hover:text-blue-200"
              >
                ← Back to form
              </button>
            </div>

            <dl className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
              <div>
                <dt className="text-[var(--color-text-muted)]">Job role</dt>
                <dd className="text-[var(--color-text)] font-medium mt-1">{jobRole || '—'}</dd>
              </div>
              <div>
                <dt className="text-[var(--color-text-muted)]">CVs</dt>
                <dd className="text-[var(--color-text)] font-medium mt-1">{n}</dd>
              </div>
              <div>
                <dt className="text-[var(--color-text-muted)]">Package</dt>
                <dd className="text-[var(--color-text)] font-medium mt-1">
                  {selectedPackCode === 'free' ? 'Free (daily quota)' : selectedPack?.name || '—'}
                </dd>
              </div>
              <div>
                <dt className="text-[var(--color-text-muted)]">Amount</dt>
                <dd className="text-[var(--color-text)] font-medium mt-1">
                  {selectedPackCode === 'free' ? '₦0' : formatNaira(estimatedAmount)}
                </dd>
              </div>
            </dl>

            {files.length === 0 ? (
              <div className="rounded-xl border border-amber-500/30 bg-amber-500/10 p-4 text-sm text-amber-50">
                Your CV files were not restored. Re-attach the same CVs below, then continue.
                <label className="mt-3 flex flex-col items-center justify-center gap-2 rounded-xl border border-dashed border-amber-500/40 elevated-card px-4 py-6 cursor-pointer hover:border-amber-400">
                  <FileUp className="w-5 h-5 text-amber-300" />
                  <span>Re-attach CVs</span>
                  <input
                    type="file"
                    accept=".pdf,.doc,.docx,.txt,application/pdf"
                    multiple
                    className="hidden"
                    onChange={onPickFiles}
                  />
                </label>
              </div>
            ) : (
              <ul className="space-y-1 text-sm text-[var(--color-text-secondary)]">
                {files.map((f) => (
                  <li key={f.name} className="truncate">
                    · {f.name}
                  </li>
                ))}
              </ul>
            )}

            {paymentStatus === 'failed' ? (
              <div className="rounded-xl border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-red-100">
                Payment failed or was cancelled. Retry checkout to unlock Compare.
              </div>
            ) : null}

            {paymentStatus === 'success' && paymentTxRef ? (
              <div className="rounded-xl border border-emerald-500/30 bg-emerald-500/10 px-4 py-3 text-sm text-emerald-100">
                Payment successful. Click Compare to run the shortlist.
              </div>
            ) : null}

            <div className="flex flex-wrap gap-3">
              {selectedPackCode === 'free' ? (
                <button
                  type="button"
                  onClick={handleCompareClick}
                  disabled={submitting || files.length < 1}
                  className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-xl bg-blue-600 hover:bg-blue-500 disabled:opacity-50 text-[var(--color-text)] font-semibold"
                >
                  {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Users className="w-4 h-4" />}
                  {submitting ? 'Queuing…' : 'Confirm & compare'}
                </button>
              ) : step === 'compare' && paymentStatus === 'success' ? (
                <button
                  type="button"
                  onClick={handleCompareClick}
                  disabled={submitting || files.length < 1}
                  className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-xl bg-blue-600 hover:bg-blue-500 disabled:opacity-50 text-[var(--color-text)] font-semibold"
                >
                  {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Users className="w-4 h-4" />}
                  {submitting ? 'Queuing…' : 'Compare'}
                </button>
              ) : (
                <button
                  type="button"
                  onClick={startCheckout}
                  disabled={Boolean(buyingPack) || files.length < 1}
                  className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-xl bg-blue-600 hover:bg-blue-500 disabled:opacity-50 text-[var(--color-text)] font-semibold"
                >
                  {buyingPack ? <Loader2 className="w-4 h-4 animate-spin" /> : <ShoppingCart className="w-4 h-4" />}
                  {buyingPack ? 'Redirecting…' : paymentStatus === 'failed' ? 'Retry payment' : 'Pay & continue'}
                </button>
              )}
            </div>

            <p className="text-xs text-[var(--color-text-muted)] flex items-center gap-1.5">
              <Mail className="w-3.5 h-3.5" />
              Full comparison arrives as a PDF attachment (short email notice only).
            </p>
            {selectedPackCode !== 'free' ? (
              <p className="text-xs text-[var(--color-text-muted)]">
                Payments are processed by TabsolTech for DocxsAi. Your statement may show TabsolTech as the merchant.
              </p>
            ) : null}
          </div>
        )}

        <div className="rounded-2xl border border-[var(--color-border)] elevated-card p-5">
          <h2 className="text-lg font-semibold text-[var(--color-text)] mb-3">Recent runs</h2>
          {runs.length === 0 ? (
            <p className="text-sm text-[var(--color-text-muted)]">No shortlist runs yet.</p>
          ) : (
            <ul className="space-y-2">
              {runs.map((r) => (
                <li
                  key={r.id}
                  className="flex flex-wrap items-center justify-between gap-2 rounded-xl border border-[var(--color-border)] elevated-card px-4 py-3 text-sm"
                >
                  <div>
                    <p className="text-[var(--color-text)] font-medium">{r.job_role}</p>
                    <p className="text-[var(--color-text-muted)] text-xs mt-0.5">
                      {r.cv_count} CV(s) · {r.pack_type} ·{' '}
                      {r.created_at ? new Date(r.created_at).toLocaleString() : ''}
                    </p>
                  </div>
                  <span
                    className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold ${
                      r.status === 'completed'
                        ? 'bg-emerald-500/15 text-emerald-300'
                        : r.status === 'failed'
                          ? 'bg-red-500/15 text-red-300'
                          : 'bg-blue-500/15 text-blue-300'
                    }`}
                  >
                    {r.status === 'completed' ? <CheckCircle2 className="w-3.5 h-3.5" /> : null}
                    {r.status}
                    {r.emailed_at ? ' · emailed' : ''}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
};

export default HiringShortlistPage;
